import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { companySchema, type CompanyFormData } from '@/lib/validators';
import { useCompany, useCompanies } from '@/composables/crm/useCompanies';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FormField } from '@/components/forms/FormField';
import { FormTextarea } from '@/components/forms/FormTextarea';
import { FormCurrencyInput } from '@/components/forms/FormCurrencyInput';
import { LoadingSpinner } from '@/components/LoadingSpinner';

export function CompanyCreatePage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { createCompany, updateCompany } = useCompanies();
  const { company, isLoading } = useCompany(id || '');

  const form = useForm<CompanyFormData>({ resolver: zodResolver(companySchema), defaultValues: { name: '', industry: '', website: '', phone: '', email: '', description: '' } });

  useEffect(() => {
    if (company && isEdit) {
      form.reset({
        name: company.name || '',
        industry: company.industry || '',
        website: company.website || '',
        phone: company.phone || '',
        email: company.email || '',
        employeeCount: company.employeeCount ?? undefined,
        annualRevenue: company.annualRevenue ?? undefined,
        description: company.description || '',
        address: company.address || '',
        city: company.city || '',
        state: company.state || '',
        country: company.country || '',
        postalCode: company.postalCode || '',
      });
    }
  }, [company, isEdit, form]);

  const onSubmit = (data: CompanyFormData) => {
    if (isEdit) {
      updateCompany.mutate({ id, data }, { onSuccess: () => navigate(`/companies/${id}`) });
    } else {
      createCompany.mutate(data, { onSuccess: () => navigate('/companies') });
    }
  };

  if (isEdit && isLoading) return <LoadingSpinner />;

  const isPending = isEdit ? updateCompany.isPending : createCompany.isPending;

  return (
    <PageContainer backTo title={isEdit ? 'Edit Company' : 'Create Company'}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <div className="grid gap-6 lg:grid-cols-2">
          <Card><CardHeader><CardTitle className="text-base">Company Info</CardTitle></CardHeader><CardContent className="space-y-4">
            <FormField form={form} name="name" label="Company Name" required /><FormField form={form} name="industry" label="Industry" /><FormField form={form} name="website" label="Website" /><FormField form={form} name="phone" label="Phone" /><FormField form={form} name="email" label="Email" type="email" /><FormField form={form} name="employeeCount" label="Employees" type="number" /><FormCurrencyInput form={form} name="annualRevenue" label="Annual Revenue" />
          </CardContent></Card>
          <Card><CardHeader><CardTitle className="text-base">Additional</CardTitle></CardHeader><CardContent className="space-y-4">
            <FormTextarea form={form} name="description" label="Description" /><FormField form={form} name="address" label="Address" /><div className="grid grid-cols-2 gap-4"><FormField form={form} name="city" label="City" /><FormField form={form} name="state" label="State" /></div><div className="grid grid-cols-2 gap-4"><FormField form={form} name="country" label="Country" /><FormField form={form} name="postalCode" label="Postal Code" /></div>
          </CardContent></Card>
        </div>
        <div className="mt-6 flex justify-end gap-4"><Button type="button" variant="outline" onClick={() => navigate(-1)}>Cancel</Button><Button type="submit" disabled={isPending}>{isPending ? (isEdit ? 'Saving...' : 'Creating...') : (isEdit ? 'Save Changes' : 'Create Company')}</Button></div>
      </form>
    </PageContainer>
  );
}
